import type { PropsWithChildren } from "react";

import type { InitialConfigType } from "@lexical/react/LexicalComposer";

import { CodeNode } from "@lexical/code";
import { LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  TRANSFORMERS,
} from "@lexical/markdown";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";

import { cn } from "../../../utils";
import { MarkdownEditorToolbar } from "./markdown-editor-toolbar";
import { CitationNode } from "./nodes/CitationNode";
import { EditableStatePlugin } from "./plugins/editable-state-plugin";
import { ShortcutsPlugin } from "./plugins/shortcuts-plugin";
import { ToolbarStateProvider } from "./toolbar-state-provider";
import { CitationTransformer } from "./transformers/citationTransformer";

interface MarkdownEditorProps extends PropsWithChildren {
  initialMarkdownContent?: string;
  editable?: boolean;
  onEditableChange?: (editable: boolean) => void;
  onChange?: (markdown: string) => void;
  onEditingComplete?: () => void;
  className?: string;
}

const citationAwareTransformers = [CitationTransformer, ...TRANSFORMERS];

export function MarkdownEditor({
  initialMarkdownContent,
  editable = false,
  onEditableChange,
  onChange,
  onEditingComplete,
  className,
}: MarkdownEditorProps) {
  return (
    <LexicalComposer
      initialConfig={{
        ...markdownEditorProviderInitialConfig,
        editable,
        editorState: initialMarkdownContent
          ? () =>
              $convertFromMarkdownString(
                initialMarkdownContent,
                citationAwareTransformers,
              )
          : undefined,
      }}
    >
      <EditableStatePlugin
        editable={editable}
        onEditableChange={onEditableChange}
        onEditingComplete={onEditingComplete}
      />
      <OnChangePlugin
        ignoreSelectionChange={true}
        onChange={(editorState) => {
          const markdown = editorState.read(() =>
            $convertToMarkdownString(citationAwareTransformers),
          );
          onChange?.(markdown);
        }}
      />
      <ListPlugin />
      <MarkdownShortcutPlugin transformers={citationAwareTransformers} />
      <TabIndentationPlugin maxIndent={7} />
      <HistoryPlugin />
      <div
        className={cn(
          "af-rounded-md af-border af-border-transparent",
          {
            "af-border-border af-bg-background af-px-3 af-py-2 focus-within:af-border-secondary":
              editable,
          },
          className,
        )}
      >
        <ToolbarStateProvider>
          {editable && <MarkdownEditorToolbar />}
          <ShortcutsPlugin />
        </ToolbarStateProvider>
        <RichTextPlugin
          placeholder={null}
          ErrorBoundary={LexicalErrorBoundary}
          contentEditable={
            <ContentEditable
              className={cn("[&[contenteditable='true']]:af-outline-none", {
                "[&>*:first-child]:af-mt-0 [&>*:first-child]:af-pt-0":
                  !editable,
              })}
            />
          }
        />
      </div>
    </LexicalComposer>
  );
}

const markdownEditorProviderInitialConfig: InitialConfigType = {
  namespace: "agent",
  onError: console.error,
  nodes: [
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    LinkNode,
    CitationNode,
  ],
  theme: {
    paragraph: "af-pt-4 af-text-sm",
    heading: {
      h1: "af-m-0 af-pt-6 af-text-2xl af-font-bold",
      h2: "af-m-0 af-pt-6 af-text-xl af-font-bold",
      h3: "af-m-0 af-pt-6 af-text-lg af-font-bold",
      h4: "af-text-md af-m-0 af-pt-6 af-font-bold",
      h5: "af-m-0 af-pt-6 af-text-sm af-font-bold",
      h6: "af-m-0 af-pt-6 af-text-xs af-font-bold",
    },
    list: {
      ol: "af-text-md af-flex af-list-inside af-flex-col af-gap-3 af-p-0",
      ul: "af-text-md af-flex af-list-inside af-flex-col af-gap-3 af-p-0",
      listitem: "af-mx-4 af-text-sm af-text-foreground",
      nested: {
        listitem: "af-list-none af-text-foreground",
      },
      ulDepth: ["af-list-disc", "af-list-[circle]", "af-list-[square]"],
      olDepth: [
        "af-list-decimal",
        "af-list-[upper-alpha]",
        "af-list-[lower-alpha]",
        "af-list-[upper-roman]",
        "af-list-[lower-roman]",
      ],
    },
    link: "af-pt-4 af-text-primary af-text-foreground",
    text: {
      code: "af-mt-6 af-text-foreground",
      italic: "af-italic af-text-foreground",
      strikethrough: "af-line-through af-text-foreground",
      underline: "af-underline af-text-foreground",
      underlineStrikethrough:
        "[text-decoration:underline_line-through] af-text-foreground",
    },
  },
};
